import classNames from 'classnames'
import { ElementType, FC, PropsWithChildren } from 'react'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { BackIcon } from '@icons/BackIcon'
import { CircleInfoLineIcon } from '@icons/CircleInfoLineIcon'
import { DownloadCircledIcon } from '@icons/DownloadCircledIcon'
import { ForwardIcon } from '@icons/ForwardIcon'

import { InfoCard, InfoCardProps } from '@ui/Popups/InfoCard/InfoCard'
import { SearchField, SearchFieldProps } from '@ui/SearchField/SearchField'
import { Title1 } from '@ui/Typography'

import { useWindowResize } from '@utils/domUtils'

import { Button } from '@global/button/Button'
import IconButton from '@global/iconButton/IconButton'

import { Modal } from '../Modal'
import { DownloadDropdown } from './DownloadDropdown'
import { PageSectionTabs, PageSectionTabsProps } from './PageSectionTabs'

import styles from './PageSection.module.scss'

export interface PageSectionProps {
  icon?: ElementType
  title: string
  renderMessage?: () => React.ReactNode
  onGoBack?: () => void
  documentFile?: {
    handleGenerateExcel: () => void
    handleGeneratePDF: () => void
    handleGenerateCSV: () => void
    tableBody: () => string[][]
    isOptionsModalOpen: boolean
    setOptionsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
    exportFilename: string
  }
  searchField?: SearchFieldProps
  renderActions?: {
    icon: ElementType
    onClick: () => void
    title?: string
    noResize?: boolean
    disabled?: boolean
  }[]
  tabData?: PageSectionTabsProps
  id?: string
  infoData?: InfoCardProps
}

export const PageSection: FC<PropsWithChildren<PageSectionProps>> = (props) => {
  const { children, renderActions, tabData, infoData, documentFile, icon: Icon, title, id } = props
  const { onGoBack, renderMessage, searchField } = props
  const isArabic = useArabicSessionLanguage()
  const isMobile = useWindowResize()

  const handleDownload = (fileType?: string) => {
    if (!isMobile) {
      switch (fileType) {
        case 'pdf':
          documentFile?.handleGeneratePDF()
          break
        case 'csv':
          documentFile?.handleGenerateCSV()
          break
        case 'excel':
          documentFile?.handleGenerateExcel()
          break
        default:
          break
      }
    } else {
      documentFile?.setOptionsModalOpen(true)
    }
  }

  const hasActions = (renderActions?.length ?? 0) > 0 || documentFile || searchField

  return (
    <>
      {documentFile?.isOptionsModalOpen && (
        <Modal.Export
          fileName={documentFile.exportFilename}
          csvData={documentFile.tableBody()}
          onExportToCSV={documentFile.handleGenerateCSV}
          onExportToPdf={documentFile.handleGeneratePDF}
          onExportToExcel={documentFile.handleGenerateExcel}
          onClose={() => documentFile.setOptionsModalOpen(false)}
        />
      )}
      <div className={styles.pageSectionWrapper}>
        <div className={styles.pageSection} id={id}>
          <div
            className={classNames(styles.titleWrapper, {
              [styles.hasTabs]: !!tabData,
            })}
          >
            <div className={styles.title}>
              {onGoBack ? (
                <IconButton.New
                  appearance='secondary'
                  size='M'
                  onClick={onGoBack}
                  className={styles.back}
                >
                  {isArabic ? <ForwardIcon /> : <BackIcon />}
                </IconButton.New>
              ) : (
                !!Icon && <Icon />
              )}

              <Title1 medium>{title}</Title1>

              {infoData && (
                <InfoCard {...infoData}>
                  <CircleInfoLineIcon color='textLowEm' />
                </InfoCard>
              )}
              {!isMobile && renderMessage?.()}
            </div>
            {tabData && (
              <div className={styles.pageTabWrapper}>
                <PageSectionTabs tabs={tabData.tabs} />
              </div>
            )}
            {hasActions && (
              <div className={styles.actions}>
                {isMobile ? (
                  <IconButton.New
                    appearance='secondary'
                    size='M'
                    className={styles.downloadButton}
                    onClick={() => handleDownload()}
                  >
                    <DownloadCircledIcon />
                  </IconButton.New>
                ) : (
                  <DownloadDropdown onDownload={handleDownload} />
                )}
                {searchField && <SearchField {...searchField} />}
                {renderActions?.map(({ title, onClick, noResize, icon: Icon, disabled }) =>
                  !title || (isMobile && !noResize) ? (
                    <IconButton.New appearance='secondary' size='M' onClick={onClick}>
                      <Icon color='foregroundPrimary' />
                    </IconButton.New>
                  ) : (
                    <Button.New
                      appearance='secondary'
                      size='XS'
                      disabled={disabled}
                      onClick={onClick}
                      renderLeftIcon={() => <Icon color='textBlack' />}
                    >
                      {title}
                    </Button.New>
                  )
                )}
              </div>
            )}
          </div>
          {children}
        </div>
        {isMobile && !!renderMessage && (
          <div className={styles.mobileMessage}>{renderMessage()}</div>
        )}
      </div>
    </>
  )
}
